import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { RingSpinner } from '@/shared/components/loader/ring-spinner';

import { cn } from '@/shared/utils/index';
import { buttonVariants } from './button-variants';

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  };

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  onClick,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  const contentGapClass = size === 'sm' ? 'gap-1.5' : 'gap-2';

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (loading) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      onClick?.(event);
    },
    [loading, onClick],
  );

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }), loading && 'pointer-events-none')}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
      {...(!asChild ? { disabled: disabled || loading } : {})}
      {...props}
    >
      <span className={cn('relative inline-flex items-center justify-center', contentGapClass)}>
        <span
          className={cn(
            'inline-flex items-center justify-center transition-all duration-200 ease-out',
            contentGapClass,
            loading ? '-translate-y-full opacity-0' : 'translate-y-0 opacity-100',
          )}
        >
          {children}
        </span>
        <span
          aria-hidden={!loading}
          className={cn(
            'pointer-events-none absolute inset-0 inline-flex items-center justify-center transition-all duration-200 ease-out',
            loading ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0',
          )}
        >
          <RingSpinner className="border-current border-t-transparent" />
        </span>
      </span>
    </Comp>
  );
}
